/**
 * WalteroReChart - Branded Recharts Wrapper Components
 * 
 * Wrapper components around Recharts library that apply Waltero brand theming
 * and provide consistent styling for bar charts, line charts, and other visualizations.
 */

import React, { useMemo, forwardRef } from 'react';
import {
  ResponsiveContainer,
  BarChart as RechartsBarChart,
  LineChart as RechartsLineChart,
  AreaChart as RechartsAreaChart,
  PieChart as RechartsPieChart,
  ScatterChart as RechartsScatterChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Bar,
  Line,
  Area,
  Pie,
  Cell,
  Scatter
} from 'recharts';
import { Box, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { 
  WALTERO_CHART_COLORS,
  WALTERO_RECHARTS_THEME,
  getChartColor,
  getStatusColor
} from '../../theme/chartTheme';
import WalteroTypography from './WalteroTypography';

/**
 * Base Waltero Chart Container
 */
const WalteroChartContainer = ({ 
  children, 
  loading, 
  loadingText = 'Loading chart data...', 
  height = 400, 
  width = '100%',
  title,
  style = {},
  ...props 
}) => {
  const muiTheme = useTheme();

  const containerStyle = useMemo(() => ({
    height: `${height}px`,
    width,
    position: 'relative',
    backgroundColor: 'transparent',
    ...style
  }), [height, width, style]);

  // Loading overlay component
  const LoadingOverlay = () => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: 1,
        zIndex: 10
      }}
    >
      <CircularProgress 
        size={32} 
        thickness={4}
        sx={{ 
          color: muiTheme.palette.secondary.main,
          mb: 2 
        }} 
      />
      <WalteroTypography variant="body2" color="secondary">
        {loadingText}
      </WalteroTypography>
    </Box>
  );

  return (
    <Box sx={containerStyle} {...props}>
      {title && (
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <WalteroTypography variant="h6" color="primary">
            {title}
          </WalteroTypography>
        </Box>
      )}
      
      {loading && <LoadingOverlay />}
      
      <Box sx={{ 
        height: title ? 'calc(100% - 48px)' : '100%', 
        width: '100%',
        opacity: loading ? 0.3 : 1,
        transition: 'opacity 0.2s ease-in-out'
      }}>
        {children}
      </Box>
    </Box>
  );
};

/**
 * Custom Waltero Tooltip Component
 */
const WalteroTooltip = ({ active, payload, label, formatter, labelFormatter, ...props }) => {
  const muiTheme = useTheme();

  if (!active || !payload || !payload.length) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        border: `1px solid ${muiTheme.palette.waltero.primary.neutralBlue}`,
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0px 4px 12px rgba(2, 56, 97, 0.12)',
        fontSize: '12px',
        fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
        color: muiTheme.palette.waltero.primary.coreBlue,
        minWidth: '120px'
      }}
    >
      {label && (
        <WalteroTypography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
          {labelFormatter ? labelFormatter(label) : label}
        </WalteroTypography>
      )}
      {payload.map((entry, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: entry.color,
              borderRadius: '50%',
              mr: 1,
              flexShrink: 0
            }}
          />
          <WalteroTypography variant="caption">
            {entry.name}: {formatter ? formatter(entry.value, entry.name) : entry.value}
          </WalteroTypography>
        </Box>
      ))}
    </Box>
  );
};

/**
 * WalteroBarChart - Branded Bar Chart
 */
export const WalteroBarChart = forwardRef(({ 
  data = [], 
  loading = false,
  title,
  height = 400,
  width = '100%',
  xAxisKey = 'name',
  yAxisKeys = ['value'],
  colors,
  showGrid = true,
  showTooltip = true,
  showLegend = false,
  tooltipFormatter,
  labelFormatter,
  style = {},
  barProps = {},
  ...props 
}, ref) => {
  const chartColors = colors || WALTERO_CHART_COLORS;
  const muiTheme = useTheme();

  return (
    <WalteroChartContainer 
      loading={loading} 
      title={title} 
      height={height} 
      width={width}
      style={style}
      {...props}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RechartsBarChart data={data} ref={ref}>
          {showGrid && (
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={muiTheme.palette.waltero.primary.backgroundBlue}
              strokeWidth={1}
            />
          )}
          
          <XAxis 
            dataKey={xAxisKey}
            stroke={muiTheme.palette.waltero.primary.neutralBlue}
            tick={{ 
              fill: muiTheme.palette.waltero.primary.mediumBlue,
              fontSize: 11,
              fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif'
            }}
            tickLine={{ stroke: muiTheme.palette.waltero.primary.neutralBlue }}
            axisLine={{ stroke: muiTheme.palette.waltero.primary.neutralBlue }}
          />
          
          <YAxis 
            stroke={muiTheme.palette.waltero.primary.neutralBlue}
            tick={{ 
              fill: muiTheme.palette.waltero.primary.mediumBlue,
              fontSize: 11,
              fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif'
            }}
            tickLine={{ stroke: muiTheme.palette.waltero.primary.neutralBlue }}
            axisLine={{ stroke: muiTheme.palette.waltero.primary.neutralBlue }}
          />
          
          {showTooltip && (
            <Tooltip 
              content={<WalteroTooltip formatter={tooltipFormatter} labelFormatter={labelFormatter} />}
            />
          )}
          
          {showLegend && (
            <Legend 
              wrapperStyle={{
                fontSize: '12px',
                fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
                color: muiTheme.palette.waltero.primary.coreBlue
              }}
            />
          )}
          
          {yAxisKeys.map((key, index) => (
            <Bar 
              key={key}
              dataKey={key}
              fill={getChartColor(index, chartColors)}
              radius={[4, 4, 0, 0]}
              {...barProps}
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    </WalteroChartContainer>
  );
});

WalteroBarChart.displayName = 'WalteroBarChart';

/**
 * WalteroLineChart - Branded Line Chart
 */
export const WalteroLineChart = forwardRef(({ 
  data = [], 
  loading = false,
  title,
  height = 400,
  width = '100%',
  xAxisKey = 'name',
  yAxisKeys = ['value'],
  colors,
  showGrid = true,
  showTooltip = true,
  showLegend = false,
  strokeWidth = 2,
  dotSize = 6,
  tooltipFormatter,
  labelFormatter,
  style = {},
  lineProps = {},
  ...props 
}, ref) => {
  const chartColors = colors || WALTERO_CHART_COLORS;
  const muiTheme = useTheme();

  return (
    <WalteroChartContainer 
      loading={loading} 
      title={title} 
      height={height} 
      width={width}
      style={style}
      {...props}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RechartsLineChart data={data} ref={ref}>
          {showGrid && (
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={muiTheme.palette.waltero.primary.backgroundBlue}
              strokeWidth={1}
            />
          )}
          
          <XAxis 
            dataKey={xAxisKey}
            stroke={muiTheme.palette.waltero.primary.neutralBlue}
            tick={{ 
              fill: muiTheme.palette.waltero.primary.mediumBlue,
              fontSize: 11,
              fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif'
            }}
            tickLine={{ stroke: muiTheme.palette.waltero.primary.neutralBlue }}
            axisLine={{ stroke: muiTheme.palette.waltero.primary.neutralBlue }}
          />
          
          <YAxis 
            stroke={muiTheme.palette.waltero.primary.neutralBlue}
            tick={{ 
              fill: muiTheme.palette.waltero.primary.mediumBlue,
              fontSize: 11,
              fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif'
            }}
            tickLine={{ stroke: muiTheme.palette.waltero.primary.neutralBlue }}
            axisLine={{ stroke: muiTheme.palette.waltero.primary.neutralBlue }}
          />
          
          {showTooltip && (
            <Tooltip 
              content={<WalteroTooltip formatter={tooltipFormatter} labelFormatter={labelFormatter} />}
            />
          )}
          
          {showLegend && (
            <Legend 
              wrapperStyle={{
                fontSize: '12px',
                fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
                color: muiTheme.palette.waltero.primary.coreBlue
              }}
            />
          )}
          
          {yAxisKeys.map((key, index) => (
            <Line 
              key={key}
              type="monotone"
              dataKey={key}
              stroke={getChartColor(index, chartColors)}
              strokeWidth={strokeWidth}
              dot={{ r: dotSize, fill: getChartColor(index, chartColors) }}
              activeDot={{ r: dotSize + 2, fill: getChartColor(index, chartColors) }}
              {...lineProps}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </WalteroChartContainer>
  );
});

WalteroLineChart.displayName = 'WalteroLineChart';

/**
 * WalteroAreaChart - Branded Area Chart
 */
export const WalteroAreaChart = forwardRef(({ 
  data = [], 
  loading = false,
  title,
  height = 400,
  width = '100%',
  xAxisKey = 'name',
  yAxisKeys = ['value'],
  colors,
  showGrid = true,
  showTooltip = true,
  showLegend = false,
  strokeWidth = 2,
  fillOpacity = 0.1,
  tooltipFormatter,
  labelFormatter,
  style = {},
  areaProps = {},
  ...props 
}, ref) => {
  const chartColors = colors || WALTERO_CHART_COLORS;
  const muiTheme = useTheme();

  return (
    <WalteroChartContainer 
      loading={loading} 
      title={title} 
      height={height} 
      width={width}
      style={style}
      {...props}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RechartsAreaChart data={data} ref={ref}>
          {showGrid && (
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={muiTheme.palette.waltero.primary.backgroundBlue}
              strokeWidth={1}
            />
          )}
          
          <XAxis 
            dataKey={xAxisKey}
            stroke={muiTheme.palette.waltero.primary.neutralBlue}
            tick={{ 
              fill: muiTheme.palette.waltero.primary.mediumBlue,
              fontSize: 11,
              fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif'
            }}
            tickLine={{ stroke: muiTheme.palette.waltero.primary.neutralBlue }}
            axisLine={{ stroke: muiTheme.palette.waltero.primary.neutralBlue }}
          />
          
          <YAxis 
            stroke={muiTheme.palette.waltero.primary.neutralBlue}
            tick={{ 
              fill: muiTheme.palette.waltero.primary.mediumBlue,
              fontSize: 11,
              fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif'
            }}
            tickLine={{ stroke: muiTheme.palette.waltero.primary.neutralBlue }}
            axisLine={{ stroke: muiTheme.palette.waltero.primary.neutralBlue }}
          />
          
          {showTooltip && (
            <Tooltip 
              content={<WalteroTooltip formatter={tooltipFormatter} labelFormatter={labelFormatter} />}
            />
          )}
          
          {showLegend && (
            <Legend 
              wrapperStyle={{
                fontSize: '12px',
                fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
                color: muiTheme.palette.waltero.primary.coreBlue
              }}
            />
          )}
          
          {yAxisKeys.map((key, index) => (
            <Area 
              key={key}
              type="monotone"
              dataKey={key}
              stroke={getChartColor(index, chartColors)}
              strokeWidth={strokeWidth}
              fill={getChartColor(index, chartColors)}
              fillOpacity={fillOpacity}
              {...areaProps}
            />
          ))}
        </RechartsAreaChart>
      </ResponsiveContainer>
    </WalteroChartContainer>
  );
});

WalteroAreaChart.displayName = 'WalteroAreaChart';

/**
 * WalteroPieChart - Branded Pie Chart
 */
export const WalteroPieChart = forwardRef(({ 
  data = [], 
  loading = false,
  title,
  height = 400,
  width = '100%',
  dataKey = 'value',
  nameKey = 'name',
  colors,
  innerRadius = 0,
  outerRadius = '80%',
  showTooltip = true,
  showLegend = true,
  tooltipFormatter,
  style = {},
  pieProps = {},
  ...props 
}, ref) => {
  const chartColors = colors || WALTERO_CHART_COLORS;
  const muiTheme = useTheme();

  return (
    <WalteroChartContainer 
      loading={loading} 
      title={title} 
      height={height} 
      width={width}
      style={style}
      {...props}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPieChart ref={ref}>
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey={nameKey}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={2}
            {...pieProps}
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={getChartColor(index, chartColors)} 
              />
            ))}
          </Pie>
          
          {showTooltip && (
            <Tooltip 
              content={<WalteroTooltip formatter={tooltipFormatter} />}
            />
          )}
          
          {showLegend && (
            <Legend 
              wrapperStyle={{
                fontSize: '12px',
                fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
                color: muiTheme.palette.waltero.primary.coreBlue
              }}
            />
          )}
        </RechartsPieChart>
      </ResponsiveContainer>
    </WalteroChartContainer>
  );
});

WalteroPieChart.displayName = 'WalteroPieChart';

export default {
  WalteroBarChart,
  WalteroLineChart,
  WalteroAreaChart,
  WalteroPieChart
};

